import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GameBoard } from '../components/GameBoard';
import { ExtendedTutorial } from '../components/ExtendedTutorial';
import { useGameStore } from '../store/gameStore';
import { useTranslation } from '../hooks/useTranslation';
import { Fighter } from '../types/fighter';
import { createFighter, getFighterMaxHealth } from '../types/fighter';
import { useMusic } from '../hooks/useMusic';

interface BattlePageState {
  fighters: Fighter[];
  isRaid: boolean;
  isBaseAttack?: boolean;
  depth: number;
  enemyCount: number;
  enemies?: Fighter[]; // Враги для нападения на базу
}

export const BattlePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslation();
  const {
    addGold,
    addResource,
    addLiberation,
    updateFighter,
    incrementRaidStat,
    usePotion,
    potions,
    extendedTutorialPhase,
    extendedTutorialStep,
    setExtendedTutorialStep,
    setExtendedTutorialPhase,
    hasSeenBattleTutorial,
    setHasSeenBattleTutorial,
    musicEnabled,
    musicVolume,
  } = useGameStore();

  const [showResult, setShowResult] = React.useState<'victory' | 'defeat' | null>(null);
  const [resultLoot, setResultLoot] = React.useState<any>(null);
  const [currentWave, setCurrentWave] = React.useState(1);
  const [isDecisionMode, setIsDecisionMode] = React.useState(false);
  const [playerFightersState, setPlayerFightersState] = React.useState<Fighter[]>([]);
  const [gameBoardKey, setGameBoardKey] = React.useState(0);
  const [showHealingModal, setShowHealingModal] = React.useState(false);

  // Фоновая музыка для боя
  useMusic({ type: 'battle', volume: musicVolume, enabled: musicEnabled });

  const state = location.state as BattlePageState | null;
  const initialFighters = state?.fighters || [];
  const isRaid = state?.isRaid || false;
  const isBaseAttack = state?.isBaseAttack || false;
  const targetDepth = state?.depth || 1;
  const providedEnemies = state?.enemies || [];

  // Инициализация бойцов при первом рендере
  React.useEffect(() => {
    if (initialFighters.length > 0 && playerFightersState.length === 0) {
      setPlayerFightersState(initialFighters);
    }
  }, [initialFighters]);

  // Запуск туториала боя при первом заходе
  React.useEffect(() => {
    if (!hasSeenBattleTutorial && extendedTutorialPhase !== 'battle') {
      setExtendedTutorialPhase('battle');
      setExtendedTutorialStep(0);
    }
  }, [hasSeenBattleTutorial, extendedTutorialPhase, setExtendedTutorialPhase, setExtendedTutorialStep]);

  // Генерируем врагов для рейда на основе таблицы составов
  const generateRaidEnemies = (depth: number): Fighter[] => {
    const enemies: Fighter[] = [];

    // Функции для создания типов врагов
    const createCrowdEnemy = (index: number): Fighter => {
      // Костерез / Костяной убийца (рандом)
      const isBoneKiller = Math.random() < 0.5;
      if (isBoneKiller) {
        // Костяной убийца: Кинжал (10) + Костяной нагрудник (13)
        return createFighter(
          `enemy-d${depth}-crowd-${index}`,
          'Костяной убийца',
          0,
          0,
          false,
          10, // Костяной кинжал
          13  // Костяной нагрудник
        );
      } else {
        // Костерез: только Кинжал (10)
        return createFighter(
          `enemy-d${depth}-crowd-${index}`,
          'Костерез',
          0,
          0,
          false,
          10  // Костяной кинжал
        );
      }
    };

    const createSupportEnemy = (index: number): Fighter => {
      // Топор-щитовик / Мутант-кинжальщик / Костяной топорщик (или Топорщик для глубины 2) / Метатель (рандом)
      const rand = Math.random();
      if (rand < 0.25) {
        // Топор-щитовик: Топор (11) + Деревянный щит (14)
        return createFighter(
          `enemy-d${depth}-support-${index}`,
          'Топор-щитовик',
          0,
          0,
          false,
          11, // Ржавый топор
          14  // Деревянный щит орка
        );
      } else if (rand < 0.5) {
        // Мутант-кинжальщик: Кинжал (10) + Кожа мутанта (18)
        return createFighter(
          `enemy-d${depth}-support-${index}`,
          'Мутант-кинжальщик',
          0,
          0,
          false,
          10, // Костяной кинжал
          undefined,
          18  // Кожа мутанта
        );
      } else if (rand < 0.75) {
        // Топорщик (для глубин 2 и 3) - только топор, без доспехов
        // Костяной топорщик (для глубин 4+) - топор + костяные доспехи
        if (depth === 2 || depth === 3) {
          return createFighter(
            `enemy-d${depth}-support-${index}`,
            'Топорщик',
            0,
            0,
            false,
            11  // Ржавый топор (без доспехов)
          );
        } else {
          return createFighter(
            `enemy-d${depth}-support-${index}`,
            'Костяной топорщик',
            0,
            0,
            false,
            11, // Ржавый топор
            undefined,
            19  // Костяные доспехи
          );
        }
      } else {
        // Метатель: Метательный томагавк (16)
        return createFighter(
          `enemy-d${depth}-support-${index}`,
          'Метатель',
          0,
          0,
          false,
          16  // Метательный томагавк
        );
      }
    };

    const createEliteEnemy = (index: number): Fighter => {
      // Каменный разрушитель / Копейщик (рандом)
      const isSpearman = Math.random() < 0.5;
      if (isSpearman) {
        // Копейщик: Орочье копьё (17)
        return createFighter(
          `enemy-d${depth}-elite-${index}`,
          'Копейщик',
          0,
          0,
          false,
          17  // Орочье копьё
        );
      } else {
        // Каменный разрушитель: Булава (12) + Каменный щит (15)
        return createFighter(
          `enemy-d${depth}-elite-${index}`,
          'Каменный разрушитель',
          0,
          0,
          false,
          12, // Каменная булава
          15  // Каменный щит
        );
      }
    };

    // Генерация врагов по глубине
    switch (depth) {
      case 1:
        // 1 враг: Костерез / Костяной убийца (рандом)
        enemies.push(createCrowdEnemy(0));
        break;

      case 2:
        // 3 врага: 1x Поддержка, 2x Массовка
        enemies.push(createSupportEnemy(0));
        enemies.push(createCrowdEnemy(0));
        enemies.push(createCrowdEnemy(1));
        break;

      case 3:
        // 5 врагов: 2x Поддержка, 3x Массовка
        enemies.push(createSupportEnemy(0));
        enemies.push(createSupportEnemy(1));
        enemies.push(createCrowdEnemy(0));
        enemies.push(createCrowdEnemy(1));
        enemies.push(createCrowdEnemy(2));
        break;

      case 4:
        // 7 врагов: 1x Элита, 3x Поддержка, 3x Массовка
        enemies.push(createEliteEnemy(0));
        enemies.push(createSupportEnemy(0));
        enemies.push(createSupportEnemy(1));
        enemies.push(createSupportEnemy(2));
        enemies.push(createCrowdEnemy(0));
        enemies.push(createCrowdEnemy(1));
        enemies.push(createCrowdEnemy(2));
        break;

      case 5:
        // 9 врагов: 2x Элита, 3x Поддержка, 4x Массовка
        enemies.push(createEliteEnemy(0));
        enemies.push(createEliteEnemy(1));
        enemies.push(createSupportEnemy(0));
        enemies.push(createSupportEnemy(1));
        enemies.push(createSupportEnemy(2));
        enemies.push(createCrowdEnemy(0));
        enemies.push(createCrowdEnemy(1));
        enemies.push(createCrowdEnemy(2));
        enemies.push(createCrowdEnemy(3));
        break;

      default:
        // Для глубин больше 5 используем логику глубины 5
        enemies.push(createEliteEnemy(0));
        enemies.push(createEliteEnemy(1));
        enemies.push(createSupportEnemy(0));
        enemies.push(createSupportEnemy(1));
        enemies.push(createSupportEnemy(2));
        enemies.push(createCrowdEnemy(0));
        enemies.push(createCrowdEnemy(1));
        enemies.push(createCrowdEnemy(2));
        enemies.push(createCrowdEnemy(3));
    }

    return enemies;
  };

  const finalizeWaveResults = (wave: number, currentFighters: Fighter[]) => {
    const alivePlayerFighters = currentFighters.filter(f => f.isPlayer && f.health > 0);

    // Сохраняем погибших (удаляем из магазина)
    const initialFighterIds = initialFighters.map(f => f.id);
    const deadFighterIds = initialFighterIds.filter(id => !alivePlayerFighters.find(af => af.id === id));
    deadFighterIds.forEach(id => useGameStore.getState().removeFighter(id));

    // Обновляем HP и опыт в магазине
    alivePlayerFighters.forEach(af => {
      // Начисление очков ранга (на основе достигнутой волны)
      let rankPointsEarned = 0;
      if (wave === 2) rankPointsEarned = 1;
      else if (wave === 3) rankPointsEarned = 3;
      else if (wave === 4) rankPointsEarned = 9;

      const currentStats = useGameStore.getState().fighters.find(f => f.id === af.id);
      if (currentStats) {
        let newPoints = currentStats.rankPoints + rankPointsEarned;
        let newRank = currentStats.rank;

        if (newRank === 0 && newPoints >= 3) { newRank = 1; newPoints = 0; }
        else if (newRank === 1 && newPoints >= 9) { newRank = 2; newPoints = 0; }

        // Обновляем здоровье при повышении ранга
        const newMaxHealth = 10 + newRank * 2;
        const currentHealth = af.health;
        // Если здоровье было на максимуме, восстанавливаем до нового максимума
        const oldMaxHealth = 10 + currentStats.rank * 2;
        const newHealth = currentHealth >= oldMaxHealth ? newMaxHealth : Math.min(currentHealth, newMaxHealth);

        updateFighter(af.id, {
          rank: newRank,
          rankPoints: newPoints,
          health: newHealth,
          maxHealth: newMaxHealth
        });
      }
    });

    // Начисление лута за волну (соответствует DEPTH_INFO)
    const lootByDepth: Record<number, { gold: number; metal: number; wood: number; leather: number; crystals: number }> = {
      1: { gold: 10, metal: 5, wood: 5, leather: 3, crystals: 1 },
      2: { gold: 25, metal: 10, wood: 10, leather: 6, crystals: 2 },
      3: { gold: 50, metal: 20, wood: 15, leather: 9, crystals: 3 },
      4: { gold: 100, metal: 35, wood: 25, leather: 12, crystals: 4 },
      5: { gold: 200, metal: 50, wood: 40, leather: 15, crystals: 5 },
    };

    const loot = lootByDepth[wave] || lootByDepth[5];
    const goldReward = loot.gold;
    const metalReward = loot.metal;
    const woodReward = loot.wood;
    const leatherReward = loot.leather;
    const crystalsReward = loot.crystals;
    const liberationReward = 1 + (wave - 1) * 2;

    addGold(goldReward);
    addResource('metal', metalReward);
    addResource('wood', woodReward);
    addResource('leather', leatherReward);
    if (crystalsReward > 0) addResource('crystals', crystalsReward);
    addLiberation(liberationReward);

    // Записываем статистику
    incrementRaidStat(wave);

    return {
      gold: goldReward,
      metal: metalReward,
      wood: woodReward,
      leather: leatherReward,
      crystals: crystalsReward,
      liberation: liberationReward
    };
  };

  const handleGameOver = (winner: 'player' | 'ai', currentFighters?: Fighter[]) => {
    // Завершаем туториал боя при завершении боя
    if (extendedTutorialPhase === 'battle') {
      setHasSeenBattleTutorial(true);
      setExtendedTutorialPhase(null);
      setExtendedTutorialStep(-1);
    }

    if (winner === 'player' && isRaid) {
      const loot = finalizeWaveResults(currentWave, currentFighters || []);
      setResultLoot(loot);
      setPlayerFightersState(currentFighters || []);
      setIsDecisionMode(true);
    } else if (winner === 'player' && isBaseAttack) {
      // Победа в нападении на базу: награда и возврат на базу
      if (currentFighters) {
        const initialFighterIds = initialFighters.map(f => f.id);
        const alivePlayerFighters = currentFighters.filter(f => f.isPlayer && f.health > 0);
        // Удаляем мертвых бойцов
        const deadFighterIds = initialFighterIds.filter(id => !alivePlayerFighters.find(af => af.id === id));
        deadFighterIds.forEach(id => useGameStore.getState().removeFighter(id));
        // Обновляем HP выживших
        alivePlayerFighters.forEach(af => useGameStore.getState().updateFighter(af.id, { health: af.health }));
      }
      // Награда за защиту базы
      const liberation = useGameStore.getState().liberation;
      const goldReward = Math.floor(liberation / 10) * 10 + 20; // Базовая награда зависит от освобождения
      addGold(goldReward);
      addResource('metal', 10);
      addResource('wood', 5);
      addResource('leather', 5);
      setResultLoot({ gold: goldReward, metal: 10, wood: 5, leather: 5 });
      setShowResult('victory');
    } else {
      // Поражение: удаляем всех кто погиб в этой волне
      if (currentFighters) {
        const initialFighterIds = initialFighters.map(f => f.id);
        const alivePlayerFighters = currentFighters.filter(f => f.isPlayer && f.health > 0);
        const deadFighterIds = initialFighterIds.filter(id => !alivePlayerFighters.find(af => af.id === id));
        deadFighterIds.forEach(id => useGameStore.getState().removeFighter(id));
        // HP выживших тоже сохраняем
        alivePlayerFighters.forEach(af => useGameStore.getState().updateFighter(af.id, { health: af.health }));
      }

      // Если это нападение на базу, теряем 20% освобождения
      if (isBaseAttack) {
        const currentLiberation = useGameStore.getState().liberation;
        const lossAmount = Math.max(0, Math.floor(currentLiberation * 0.2));
        if (lossAmount > 0) {
          addLiberation(-lossAmount);
        }
      }

      setShowResult('defeat');
    }
  };

  const handleContinue = () => {
    setIsDecisionMode(false);
    setResultLoot(null);
    // Показываем окно лечения зелёным зельем перед следующей волной
    if (isRaid && currentWave < targetDepth && (potions?.greenPotion || 0) > 0) {
      setShowHealingModal(true);
    } else {
      setCurrentWave(prev => prev + 1);
      setGameBoardKey(prev => prev + 1);
    }
  };

  const handleHealFighter = (fighterId: string) => {
    if (usePotion('greenPotion')) {
      const fighter = playerFightersState.find(f => f.id === fighterId);
      if (fighter) {
        const maxHealth = getFighterMaxHealth(fighter);
        const updatedFighters = playerFightersState.map(f =>
          f.id === fighterId ? { ...f, health: maxHealth } : f
        );
        setPlayerFightersState(updatedFighters);
        updateFighter(fighterId, { health: maxHealth });
      }
    }
  };

  const handleSkipHealing = () => {
    setShowHealingModal(false);
    setCurrentWave(prev => prev + 1);
    setGameBoardKey(prev => prev + 1);
  };

  const closeResultAndExit = () => {
    // Завершаем туториал боя при выходе из боя
    if (extendedTutorialPhase === 'battle') {
      setHasSeenBattleTutorial(true);
      setExtendedTutorialPhase(null);
      setExtendedTutorialStep(-1);
    }
    navigate('/base');
  };

  // Генерируем врагов для текущей волны
  const currentEnemies = isRaid
    ? generateRaidEnemies(currentWave)
    : isBaseAttack
      ? providedEnemies
      : undefined;

  // Если нет состояния, перенаправляем на базу
  if (!state) {
    // Завершаем туториал боя при выходе из боя
    if (extendedTutorialPhase === 'battle') {
      setHasSeenBattleTutorial(true);
      setExtendedTutorialPhase(null);
      setExtendedTutorialStep(-1);
    }
    navigate('/base');
    return null;
  }

  return (
    <div className="bg-black flex flex-col items-center justify-center relative overflow-hidden" style={{
      minHeight: 'calc(100vh - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0))',
      width: 'calc(100vw - env(safe-area-inset-left, 0) - env(safe-area-inset-right, 0))',
      paddingTop: 'env(safe-area-inset-top, 0)',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      paddingLeft: 'env(safe-area-inset-left, 0)',
      paddingRight: 'env(safe-area-inset-right, 0)',
    }}>
      <GameBoard
        key={gameBoardKey}
        playerFighters={playerFightersState.length > 0 ? playerFightersState : initialFighters}
        mapSize={isBaseAttack ? { width: 15, height: 15 } : { width: 10, height: 10 }}
        onGameOver={handleGameOver}
        aiFighters={currentEnemies}
      />

      {/* Расширенный туториал боя */}
      {extendedTutorialPhase === 'battle' && extendedTutorialStep >= 0 && (
        <ExtendedTutorial
          phase={extendedTutorialPhase}
          currentStep={extendedTutorialStep}
          onStepComplete={() => {
            // Получаем количество шагов для этапа боя
            const battleStepsCount = 13; // Обновлено количество шагов (добавлен шаг о препятствиях)
            if (extendedTutorialStep < battleStepsCount - 1) {
              setExtendedTutorialStep(extendedTutorialStep + 1);
            } else {
              // Завершаем туториал боя
              setHasSeenBattleTutorial(true);
              setExtendedTutorialPhase(null);
              setExtendedTutorialStep(-1);
            }
          }}
          onPhaseComplete={() => {
            // Завершаем туториал боя
            setHasSeenBattleTutorial(true);
            setExtendedTutorialPhase(null);
            setExtendedTutorialStep(-1);
          }}
        />
      )}

      {(showResult || isDecisionMode) && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 font-pixel">
          <div className={`p-8 border-4 border-yellow-500 bg-yellow-900/40 rounded-lg text-center max-w-lg w-full ${showResult === 'defeat' ? 'border-red-600 bg-red-900/40' : ''}`}>
            {showResult === 'defeat' ? (
              <h2 className="text-4xl mb-6 text-red-500 uppercase">{t.battle.defeat}</h2>
            ) : (
              <h2 className="text-3xl mb-6 text-yellow-400 uppercase">
                {isBaseAttack ? t.battle.baseDefended : currentWave === targetDepth ? t.battle.raidCompleted : t.battle.waveCompleted.replace('{wave}', currentWave.toString())}
              </h2>
            )}

            {resultLoot && (
              <div className="text-left space-y-2 mb-8 bg-black/40 p-4 border border-yellow-500/30 rounded">
                <p className="text-sm text-yellow-200 mb-4 border-b border-yellow-500/30 pb-2 uppercase tracking-tighter">
                  {isBaseAttack ? t.battle.defenseReward : t.battle.waveTrophies.replace('{wave}', currentWave.toString())}
                </p>
                <div className="flex justify-between text-xs"><span>{t.battle.gold}:</span> <span className="text-yellow-400">+{resultLoot.gold}</span></div>
                <div className="flex justify-between text-xs"><span>{t.battle.metal}:</span> <span className="text-gray-300">+{resultLoot.metal}</span></div>
                <div className="flex justify-between text-xs"><span>{t.battle.wood}:</span> <span className="text-orange-400">+{resultLoot.wood}</span></div>
                {resultLoot.leather > 0 && <div className="flex justify-between text-xs"><span>{t.battle.leather}:</span> <span className="text-amber-600">+{resultLoot.leather}</span></div>}
                {resultLoot.crystals > 0 && <div className="flex justify-between text-xs"><span>{t.battle.crystals}:</span> <span className="text-blue-400">+{resultLoot.crystals}</span></div>}
                {resultLoot.liberation > 0 && (
                  <div className="flex justify-between font-bold mt-4 pt-2 border-t border-white/10 text-xs">
                    <span>{t.battle.liberation}:</span> <span className="text-green-400">+{resultLoot.liberation}%</span>
                  </div>
                )}
              </div>
            )}

            {isDecisionMode && !isBaseAttack && (
              <div className="mb-8">
                <div className="text-white text-xs mb-4 uppercase text-left border-b border-white/10 pb-2">{t.battle.squadStatus}</div>
                <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                  {playerFightersState.filter(f => f.isPlayer).map(f => (
                    <div key={f.id} className="flex justify-between items-center text-[10px] bg-black/20 p-2 border border-white/5">
                      <span className={f.health <= 0 ? 'text-red-500' : 'text-white'}>{f.name}</span>
                      <span className={f.health <= 0 ? 'text-red-600' : 'text-green-400'}>
                        {f.health <= 0 ? t.battle.killed : `${f.health}/${getFighterMaxHealth(f)} ❤️`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              {isDecisionMode && currentWave < targetDepth && (
                <button
                  onClick={handleContinue}
                  className="flex-1 py-4 bg-green-700 hover:bg-green-600 border-2 border-green-500 text-white font-bold text-sm shadow-[0_4px_0_0_rgba(20,100,20,1)] active:translate-y-1 active:shadow-none transition-all"
                >
                  {t.battle.continueBattle.replace('{wave}', (currentWave + 1).toString())}
                </button>
              )}

              <button
                onClick={closeResultAndExit}
                className={`py-4 px-8 font-bold text-sm transition-all shadow-[0_4px_0_0_rgba(100,0,0,1)] active:translate-y-1 active:shadow-none ${isDecisionMode ? 'flex-1 bg-yellow-700 hover:bg-yellow-600 text-black border-2 border-yellow-500 shadow-[0_4px_0_0_rgba(150,100,0,1)]' :
                  'w-full bg-red-700 hover:bg-red-600 text-white border-2 border-red-500'
                  }`}
              >
                {isDecisionMode ? t.battle.takeAndRetreat : t.battle.returnToBase}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Окно лечения зелёным зельем между боями */}
      {showHealingModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 font-pixel">
          <div className="p-8 border-4 border-green-500 bg-green-900/40 rounded-lg text-center max-w-lg w-full">
            <h2 className="text-3xl mb-6 text-green-400 uppercase">Лечение зелёным зельем</h2>
            <div className="mb-4 text-sm text-white/80">
              Зелёных зелий: <span className="text-green-400 font-bold">{potions?.greenPotion || 0}</span>
            </div>
            <div className="mb-6">
              <div className="text-white text-xs mb-4 uppercase text-left border-b border-white/10 pb-2">Выберите юнита для лечения:</div>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                {playerFightersState.filter(f => f.isPlayer && f.health < getFighterMaxHealth(f)).map(f => (
                  <div key={f.id} className="flex justify-between items-center text-[10px] bg-black/20 p-2 border border-white/5">
                    <span className={f.health <= 0 ? 'text-red-500' : 'text-white'}>{f.name}</span>
                    <div className="flex items-center gap-2">
                      <span className={f.health <= 0 ? 'text-red-600' : 'text-green-400'}>
                        {f.health}/{getFighterMaxHealth(f)} ❤️
                      </span>
                      <button
                        onClick={() => handleHealFighter(f.id)}
                        disabled={(potions?.greenPotion || 0) === 0 || f.health >= getFighterMaxHealth(f)}
                        className="px-3 py-1 bg-green-700 hover:bg-green-600 border border-green-500 text-white text-[8px] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        💚 Лечить
                      </button>
                    </div>
                  </div>
                ))}
                {playerFightersState.filter(f => f.isPlayer && f.health < getFighterMaxHealth(f)).length === 0 && (
                  <div className="text-gray-400 text-xs py-4">Все юниты полностью здоровы</div>
                )}
              </div>
            </div>
            <button
              onClick={handleSkipHealing}
              className="w-full py-4 bg-yellow-700 hover:bg-yellow-600 border-2 border-yellow-500 text-black font-bold text-sm shadow-[0_4px_0_0_rgba(150,100,0,1)] active:translate-y-1 active:shadow-none transition-all"
            >
              ПРОПУСТИТЬ
            </button>
          </div>
        </div>
      )}

      <style>{`
        .font-pixel { font-family: 'Press Start 2P', cursive; text-transform: uppercase; }
      `}</style>
    </div>
  );
};

